import { useEffect, useRef, useState } from 'react';
import { abbreviate } from '@chainflip/utils/string';
import { Link } from '@/shared/components/atoms/Link';
import AccountIdInput, {
  useAccountIdInputControls,
} from '@/shared/components/dashboards/AccountIdInput';
import {
  DashboardModalWithCloseButton,
  ModalContent,
  StatsPanel,
} from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import useAccountInfo, { type AccountInfoWithId } from '@/shared/hooks/useAccountInfo';
import useBoolean from '@/shared/hooks/useBoolean';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useToast from '@/shared/hooks/useToast';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { humanReadableRole } from '@/shared/utils/rpc';
import TokenAmount from '@/shared/utils/TokenAmount';
import { RegisterNodeModalContent } from './RegisterNodeModal';
import { useExtrinsicMutation } from '../../../hooks/useStateChainAccount';

const AccountIdView = ({
  onClaimed,
  onFund,
  funded,
}: {
  onClaimed: (accountId: AccountInfoWithId) => void;
  onFund: (accountId: AccountInfoWithId) => void;
  funded: boolean;
}) => {
  const { selectedAccount } = usePolkadot(true);
  const controls = useAccountIdInputControls();
  const toast = useToast('account-id');

  const {
    data,
    isLoading: accountInfoLoading,
    error,
  } = useAccountInfo(controls.validatedId, {
    refetchInterval: 5_000,
  });

  useEffect(() => {
    if (error) toast.error('Failed to fetch account info');

    return () => toast.dismiss();
  }, [error]);

  const claimToast = useToast('claim-validator');
  const { mutate: claimValidator, isPending: claimPending } = useExtrinsicMutation(
    'operator_claimValidator',
    {
      onMutate() {
        claimToast.loading('Submitting transaction to the State Chain...');
      },
      onSuccess() {
        claimToast.success('Successfully claimed validator');
        onClaimed(data!);
      },
      onError(err) {
        claimToast.error('Error while claiming Validator:', {
          description: err.message,
        });
      },
    },
  );

  const balanceRef = useRef(data?.flip_balance);

  useEffect(() => {
    if (funded && data?.flip_balance === balanceRef.current) {
      toast.loading('Waiting for account to be funded...', {
        description: `Please be patient while your ${FLIP_SYMBOL} is credited to the account.`,
      });
    }

    return () => toast.dismiss();
  }, [funded, data?.flip_balance]);

  useEffect(() => {
    if (balanceRef.current === undefined) {
      balanceRef.current = data?.flip_balance;
    }
  }, [data?.flip_balance]);

  return (
    <ModalContent
      className="w-[460px]"
      title="Claim Validator"
      buttons={
        <>
          <Button
            size="medium"
            type="secondary-standard"
            disabled={!data || data.role !== 'unregistered'}
            onClick={() => onFund(data!)}
          >
            Fund Validator
          </Button>
          <Button
            size="medium"
            type="primary-standard"
            loading={claimPending}
            disabled={data !== undefined && data.role !== 'validator'}
            onClick={() => {
              if (!controls.validatedId) {
                controls.setError('Please provide a valid public key');
                return;
              }
              if (data?.role !== 'validator') {
                toast.error('Only Validator accounts can be claimed');
                return;
              }

              if (data?.operator) {
                if (data.operator === selectedAccount.idSs58) {
                  toast.error('You have already claimed this Validator');
                } else {
                  toast.error('This Validator is already claimed by another Operator');
                }
                return;
              }

              claimValidator(controls.validatedId);
            }}
          >
            Claim Validator
          </Button>
        </>
      }
      description="Please enter the public key of the Validator you want to claim."
    >
      <div className="flex flex-col gap-y-6">
        <AccountIdInput {...controls} />
        <StatsPanel
          items={[
            {
              label: 'Alias',
              content: data && (data.vanity_name || 'Unnamed'),
              loading: accountInfoLoading,
            },
            {
              label: 'Balance',
              flipAmount: data && TokenAmount.fromAsset(data.flip_balance, 'Flip'),
              loading: accountInfoLoading,
            },
            {
              label: 'Role',
              content: data && humanReadableRole[data.role],
              loading: accountInfoLoading,
            },
          ]}
        />
        {data && data.role === 'unregistered' && (
          <Callout type="warning">
            This account must first be funded and{' '}
            <Link
              underline
              href="https://docs.chainflip.io/validators/mainnet/validator-setup"
              color="orange"
            >
              registered as a Validator
            </Link>{' '}
            before you can claim it.
          </Callout>
        )}
        {data && data.role === 'validator' && (
          <Callout type="info">The validator needs to accept your claim to be successful</Callout>
        )}
      </div>
    </ModalContent>
  );
};

export default function ClaimValidatorModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { value: claimed, setTrue: setClaimed, setFalse: setNotclaimed } = useBoolean(false);
  const { value: funded, setTrue: setFunded, setFalse: setNotFunded } = useBoolean(false);
  const { value: funding, setTrue: setFunding, setFalse: setNotFunding } = useBoolean(false);
  const [account, setAccount] = useState<AccountInfoWithId>();

  const reset = () => {
    setAccount(undefined);
    setNotclaimed();
    setNotFunded();
    setNotFunding();
  };

  let children;

  if (claimed) {
    children = (
      <ModalContent
        title="Validator Claimed"
        description={
          <span className="text-16">
            You claimed the Validator {account?.vanity_name} ({abbreviate(account!.id)}).
          </span>
        }
        success
        buttons={
          <Button size="medium" fullWidth type="primary-standard" onClick={onClose}>
            Return to Dashboard
          </Button>
        }
      >
        <Callout type="info">
          The Validator needs to accept the claim before it will appear in your account.
        </Callout>
      </ModalContent>
    );
  } else if (account && funding) {
    children = (
      <RegisterNodeModalContent
        nodeAddress={account.id}
        onFunded={() => {
          setFunded();
          setNotFunding();
        }}
      />
    );
  } else {
    children = (
      <AccountIdView
        onClaimed={(acct) => {
          setClaimed();
          setAccount(acct);
        }}
        onFund={(acct) => {
          setNotFunded();
          setAccount(acct);
          setFunding();
        }}
        funded={funded}
      />
    );
  }

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose} afterLeave={reset}>
      {children}
    </DashboardModalWithCloseButton>
  );
}
